import { cloneDeep } from 'lodash'
import router from '@/xreports/router'
import store from '@/xreports/store'
import { asyncRoutes } from '@/xreports/router/routes'
import * as AppBaseConfigService from '@/storage/services/AppBaseConfigService'

/**
 * 将菜单权限集合转成Map
 * @param {Array} roles 菜单权限集合
 * @returns
 */
function rolesToMap(roles = []) {
  return roles.reduce((obj, role) => {
    if (role?.children?.length) {
      obj = {
        ...obj,
        ...rolesToMap(role.children),
      }
    }
    obj[role.code] = role
    return obj
  }, {})
}

/**
 * 过滤没有权限的routes
 * @param routes asyncRoutes
 * @param roles
 */
function filterAsyncRoutes(routes = [], rolesMap) {
  let res = []
  for (const route of routes) {
    if (route.children?.length) {
      route.children = filterAsyncRoutes(route.children, rolesMap)
    }
    if (rolesMap[route.name]) {
      res.push(route)
    }
  }
  return res
}

/**
 * 特殊处理首页
 * @param routes accessedRoutes
 */
function formatterHomeRoutes(accessedRoutes, defaultDashboardUid = 'empty') {
  let hasIndex = false
  let hasDashboard = false
  for (const item of accessedRoutes) {
    if (item.path === '/' && item.children?.length) {
      item.redirect = item.children[0].path
      hasIndex = true
    }
    if (['CUSTOMIZED-DASHBOARD-EMPTY', 'CUSTOMIZED-DASHBOARD-INDEX'].includes(item.name)) {
      hasDashboard = true
    }
  }

  if (hasIndex) {
    accessedRoutes.push({ path: '*', redirect: '/' })
  } else if (hasDashboard) {
    accessedRoutes.push({ path: '/', redirect: `/configuration-center/board/${defaultDashboardUid}` })
    accessedRoutes.push({ path: '*', redirect: '/' })
  }
  return accessedRoutes
}

/**
 * 格式化返回的routes
 * @param {Array} routes 服务端返回routes
 * @param {Array} menuDashboardList 菜单中的看板列表
 * @returns
 */
const formatterRoutes = (routes, menuDashboardList, filterHide) => {
  const accessRoutes = []
  if (!routes?.map) {
    return accessRoutes
  }
  const hideInMenuCodes = [
    'DATA-CENTER-TABLE-DATA-DETAIL',
    'ACTION-RECORD-WARNING-RULES-DETAIL',
    'ACTION-RECORD-WARNING-RECORD-DETAIL',
    'CONFIGURATION-CENTER-TEMPLATE-LIBRARIES',
    'CONFIGURATION-CENTER-TEMPLATES',
    'ANALYSIS-DASHBOARD-INDEX',
  ]

  for (const item of routes) {
    if (filterHide) {
      if (hideInMenuCodes.includes(item.code)) {
        continue
      }
      if (item.positionName === 'hide') {
        continue
      }
    }
    if (menuDashboardList?.length) {
      if (item.code === 'CUSTOMIZED-DASHBOARD-EMPTY') {
        continue
      } else if (['ANALYSIS-DASHBOARD', 'CUSTOMIZED-DASHBOARD-INDEX'].includes(item.code)) {
        let prefix = '/configuration-center/board/'
        if ('ANALYSIS-DASHBOARD' === item.code) {
          prefix = '/analysis-dashboard/dashboard/'
        }
        const childrenList = menuDashboardList.map(item => ({
          name: item.dashboardName,
          code: item.dashboardUid,
          path: `${prefix}${item.dashboardUid}`,
          source: item.source,
          remark: item.remark,
        }))
        if (item.children?.length) {
          item.children = childrenList.concat(item.children)
        } else {
          item.children = childrenList
        }
      }
    } else if (['CUSTOMIZED-DASHBOARD-INDEX'].includes(item.code) && !item.children?.length) {
      continue
    }
    accessRoutes.push({
      code: item.code, // 唯一key
      path: item.path, // 路由地址
      name: item.name, // 路由名称
      alias: item.alias || item.name, // 英文名：如果英文没有翻译，直接用中文
      children: formatterRoutes(item.children, null, filterHide),
      source: item.source,
      remark: item.remark,
    })
  }
  return accessRoutes
}

/**
 * 4X菜单管理Store
 */
export default {
  namespaced: true,
  state: {
    // 菜单是否已经初始化
    initialized: false,
    // 菜单列表
    menuList: [],
    // 包含隐藏的菜单列表
    breadcrumbRoutes: [],
    // 接口返回原始数据
    menuRoutes: [],
    // 动态面包屑名称
    dynamicBreadcrumbName: '',
    // 面包屑名称
    breadcrumbName: '',
  },
  mutations: {
    SET_DYNAMIC_BREADCRUMB_NAME(state, payload) {
      state.dynamicBreadcrumbName = payload
    },
    SET_BREADCRUMB_NAME(state, payload) {
      state.breadcrumbName = payload
    },
    SET_MENU_ROUTES(state, payload) {
      state.menuRoutes = payload
    },
    SET_BREADCRUMB_ROUTES(state, payload) {
      state.breadcrumbRoutes = payload
    },
    SET_MENU_LIST(state, payload) {
      // 首页特殊处理
      let homeRouteIndex = payload.findIndex(item => item.path === '/')
      let homeRoute = payload[homeRouteIndex]
      if (homeRoute?.children?.length === 1) {
        payload[homeRouteIndex] = homeRoute.children[0]
        payload[homeRouteIndex].name = homeRoute.name
      }
      // 菜单仅支持两层
      for (const item of payload) {
        if (item.children?.length) {
          for (const obj of item.children) {
            delete obj.children
          }
        }
      }
      if (!payload?.length) {
        store.dispatch('systemUser/logout')
        return
      }

      state.menuList = payload
      AppBaseConfigService.resetAppMenus(payload)
    },
    SET_MENU_INITIALIZED(state, payload) {
      state.initialized = payload
    },
  },
  actions: {
    /**
     * 初始化系统菜单和添加异步菜单逻辑, 仅在vue-router中调用一次
     * @param {Object} StoreContext
     */
    async initMenuList({ commit, state }) {
      if (state.initialized) {
        return
      }

      const API_SERVICE_MENU = require('@/xreports/apis/system/menu').default
      const API_SERVICE_DASHBOARD = require('@/xreports/apis/dashboard/dashboard').default
      const promiseList = [API_SERVICE_MENU.getRoutes(), API_SERVICE_DASHBOARD.fetchDashboards()]
      let [routes, menuDashboardList] = await Promise.all(promiseList)
      commit('SET_MENU_ROUTES', routes)

      let accessedRoutes = filterAsyncRoutes(asyncRoutes, rolesToMap(routes))
      // 特殊处理首页
      let defaultDashboardUid = menuDashboardList?.length > 0 ? menuDashboardList[0].dashboardUid : undefined
      accessedRoutes = formatterHomeRoutes(accessedRoutes, defaultDashboardUid)
      router.addRoutes(accessedRoutes)
      commit('SET_MENU_LIST', formatterRoutes(cloneDeep(routes), menuDashboardList, true))
      commit('SET_BREADCRUMB_ROUTES', formatterRoutes(cloneDeep(routes), menuDashboardList))
      commit('SET_MENU_INITIALIZED', true)
    },
    async updateMenuList({ state, commit }) {
      const API_SERVICE_DASHBOARD = require('@/xreports/apis/dashboard/dashboard').default
      const menuDashboardList = await API_SERVICE_DASHBOARD.fetchDashboards()
      commit('SET_MENU_LIST', formatterRoutes(cloneDeep(state.menuRoutes), menuDashboardList, true))
      commit('SET_BREADCRUMB_ROUTES', formatterRoutes(cloneDeep(state.menuRoutes), menuDashboardList))
    },
  },
}
